import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

const initialState = {
  individualRehabPlanTwos: [],
  currentIndividualRehabPlanTwo: null,
  isLoading: false,
  error: null,
};

export const fetchIndividualRehabPlanTwos = createAsyncThunk(
  'individualRehabPlanTwo/fetchAll',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/individual-rehab-plan-twos/${patientId}`
        : '/individual-rehab-plan-twos';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Individual Rehab Plan (2)'
      );
    }
  }
);

export const fetchIndividualRehabPlanTwo = createAsyncThunk(
  'individualRehabPlanTwo/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/individual-rehab-plan-twos/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Individual Rehab Plan (2)'
      );
    }
  }
);

export const createIndividualRehabPlanTwo = createAsyncThunk(
  'individualRehabPlanTwo/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/individual-rehab-plan-twos', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Individual Rehab Plan (2)'
      );
    }
  }
);

export const updateIndividualRehabPlanTwo = createAsyncThunk(
  'individualRehabPlanTwo/update',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/individual-rehab-plan-twos/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Individual Rehab Plan (2)'
      );
    }
  }
);

export const deleteIndividualRehabPlanTwo = createAsyncThunk(
  'individualRehabPlanTwo/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/individual-rehab-plan-twos/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Individual Rehab Plan (2)'
      );
    }
  }
);

const individualRehabPlanTwoSlice = createSlice({
  name: 'individualRehabPlanTwo',
  initialState,
  reducers: {
    clearCurrentIndividualRehabPlanTwo: (state) => {
      state.currentIndividualRehabPlanTwo = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIndividualRehabPlanTwos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIndividualRehabPlanTwos.fulfilled, (state, action) => {
        state.isLoading = false;
        state.individualRehabPlanTwos = action.payload;
      })
      .addCase(fetchIndividualRehabPlanTwos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchIndividualRehabPlanTwo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIndividualRehabPlanTwo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentIndividualRehabPlanTwo = action.payload;
      })
      .addCase(fetchIndividualRehabPlanTwo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createIndividualRehabPlanTwo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createIndividualRehabPlanTwo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.individualRehabPlanTwos.unshift(action.payload);
      })
      .addCase(createIndividualRehabPlanTwo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateIndividualRehabPlanTwo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateIndividualRehabPlanTwo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentIndividualRehabPlanTwo = action.payload;
        const index = state.individualRehabPlanTwos.findIndex(
          (r) => r.id === action.payload.id
        );
        if (index !== -1) {
          state.individualRehabPlanTwos[index] = action.payload;
        }
      })
      .addCase(updateIndividualRehabPlanTwo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteIndividualRehabPlanTwo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteIndividualRehabPlanTwo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.individualRehabPlanTwos = state.individualRehabPlanTwos.filter(
          (r) => r.id !== action.payload.id
        );
      })
      .addCase(deleteIndividualRehabPlanTwo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentIndividualRehabPlanTwo, clearError } =
  individualRehabPlanTwoSlice.actions;
export default individualRehabPlanTwoSlice.reducer;
